import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/contexts/LanguageContext";
import { Sparkles, RefreshCw, Plus, X, Users, Heart, Star, Package, Shield, Rocket } from "lucide-react";
// animations removed
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CanvasSection = ({ title, icon: Icon, items, color, onAdd, onRemove, addPlaceholder }) => {
  const [newItem, setNewItem] = useState("");

  const handleAdd = () => {
    if (newItem.trim()) {
      onAdd(newItem.trim());
      setNewItem("");
    }
  };

  return (
    <div className={`canvas-section ${color}`}>
      <div className="canvas-section-title">
        <Icon className="w-4 h-4" />
        {title}
      </div>
      <div className="space-y-2 flex-1">
        {items?.map((item, index) => (
          <div
            key={index}
            className="canvas-item bg-white/80 flex items-center justify-between group"
          >
            <span className="flex-1 text-sm text-stone-700">{item}</span>
            <button
              onClick={() => onRemove(index)}
              className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-100 rounded"
            >
              <X className="w-3 h-3 text-red-500" />
            </button>
          </div>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <Input
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          placeholder={addPlaceholder}
          className="text-sm h-8"
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
        />
        <Button
          size="sm"
          variant="ghost"
          onClick={handleAdd}
          className="h-8 px-2"
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

const ValuePropositionStep = ({ project, setProject, saveProject, goToStep }) => {
  const { t, language } = useLanguage();
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [valueProps, setValueProps] = useState({
    customer_profile: {
      jobs: [],
      pains: [],
      gains: [],
    },
    value_map: {
      products_services: [],
      pain_relievers: [],
      gain_creators: [],
    },
  });

  useEffect(() => {
    if (project?.value_proposition) {
      setValueProps(project.value_proposition);
      setHasGenerated(true);
    }
  }, []);

  const generateValueProp = async () => {
    if (!project?.selected_idea) {
      toast.error(t('common_error'));
      return;
    }

    setIsGenerating(true);
    try {
      const response = await fetch(`${API}/generate/value-proposition`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          problem_statement: project.problem_statement,
          selected_idea: project.selected_idea,
          concept: project.concept,
          language,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setValueProps(data);
        setHasGenerated(true);
        
        await saveProject({ 
          value_proposition: data,
          current_step: 4
        }, false);
        
        setProject(prev => ({ ...prev, value_proposition: data }));
        toast.success(t('common_success'));
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common_error'));
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error(t('common_error'));
    } finally {
      setIsGenerating(false);
    }
  };

  const updateSection = (profile, section, items) => {
    const updated = {
      ...valueProps,
      [profile]: {
        ...valueProps[profile],
        [section]: items,
      },
    };
    setValueProps(updated);
  };

  const handleSave = async () => {
    await saveProject({ 
      value_proposition: valueProps,
      current_step: 4
    });
    setProject(prev => ({ ...prev, value_proposition: valueProps }));
  };

  const handleContinue = async () => {
    await handleSave();
    goToStep(5);
  };

  const addItem = (profile, section, item) => {
    const items = [...(valueProps[profile]?.[section] || []), item];
    updateSection(profile, section, items);
  };

  const removeItem = (profile, section, index) => {
    const items = valueProps[profile]?.[section]?.filter((_, i) => i !== index) || [];
    updateSection(profile, section, items);
  };

  return (
    <div className="h-full flex flex-col overflow-auto">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 px-4 sm:px-8 py-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="font-heading text-2xl font-semibold text-stone-900">
              {t('vpc_title')}
            </h2>
            <p className="text-stone-600">
              {t('vpc_description')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={generateValueProp}
              disabled={isGenerating}
              variant={hasGenerated ? "outline" : "default"}
              className={hasGenerated ? "btn-secondary" : "btn-primary"}
              data-testid="generate-vpc-btn"
            >
              {isGenerating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  {t('common_generating')}
                </>
              ) : hasGenerated ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {t('vpc_regenerate')}
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  {t('vpc_generate')}
                </>
              )}
            </Button>
            {hasGenerated && (
              <Button
                onClick={handleContinue}
                className="btn-primary"
                data-testid="continue-to-bmc-btn"
              >
                {t('vpc_continue')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1 p-4 sm:p-8">
        <div className="max-w-7xl mx-auto">
          {!hasGenerated ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-accent-blue rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-10 h-10 text-blue-600" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-stone-900 mb-2">
                {t('vpc_generate')}
              </h3>
              <p className="text-stone-600 mb-6 max-w-md mx-auto">
                {t('vpc_description')}
              </p>
              <Button
                onClick={generateValueProp}
                disabled={isGenerating}
                className="btn-primary"
                data-testid="generate-vpc-empty-btn"
              >
                {isGenerating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    {t('common_generating')}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    {t('vpc_generate')}
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Customer Profile */}
              <div className="bg-accent-blue/20 rounded-2xl p-6 border border-blue-200">
                <h3 className="font-heading text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  {t('vpc_customer_profile')}
                </h3>
                <div className="grid gap-4">
                  <CanvasSection
                    title={t('vpc_jobs')}
                    icon={Users}
                    items={valueProps.customer_profile?.jobs}
                    color="border-blue-200"
                    onAdd={(item) => addItem("customer_profile", "jobs", item)}
                    onRemove={(idx) => removeItem("customer_profile", "jobs", idx)}
                    addPlaceholder={t('vpc_add_item')}
                  />
                  <CanvasSection
                    title={t('vpc_pains')}
                    icon={Heart}
                    items={valueProps.customer_profile?.pains}
                    color="border-red-200"
                    onAdd={(item) => addItem("customer_profile", "pains", item)}
                    onRemove={(idx) => removeItem("customer_profile", "pains", idx)}
                    addPlaceholder={t('vpc_add_item')}
                  />
                  <CanvasSection
                    title={t('vpc_gains')}
                    icon={Star}
                    items={valueProps.customer_profile?.gains}
                    color="border-yellow-200"
                    onAdd={(item) => addItem("customer_profile", "gains", item)}
                    onRemove={(idx) => removeItem("customer_profile", "gains", idx)}
                    addPlaceholder={t('vpc_add_item')}
                  />
                </div>
              </div>

              {/* Value Map */}
              <div className="bg-accent-green/20 rounded-2xl p-6 border border-green-200">
                <h3 className="font-heading text-lg font-semibold text-green-900 mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  {t('vpc_value_map')}
                </h3>
                <div className="grid gap-4">
                  <CanvasSection
                    title={t('vpc_products')}
                    icon={Package}
                    items={valueProps.value_map?.products_services}
                    color="border-green-200"
                    onAdd={(item) => addItem("value_map", "products_services", item)}
                    onRemove={(idx) => removeItem("value_map", "products_services", idx)}
                    addPlaceholder={t('vpc_add_item')}
                  />
                  <CanvasSection
                    title={t('vpc_pain_relievers')}
                    icon={Shield}
                    items={valueProps.value_map?.pain_relievers}
                    color="border-emerald-200"
                    onAdd={(item) => addItem("value_map", "pain_relievers", item)}
                    onRemove={(idx) => removeItem("value_map", "pain_relievers", idx)}
                    addPlaceholder={t('vpc_add_item')}
                  />
                  <CanvasSection
                    title={t('vpc_gain_creators')}
                    icon={Rocket}
                    items={valueProps.value_map?.gain_creators}
                    color="border-teal-200"
                    onAdd={(item) => addItem("value_map", "gain_creators", item)}
                    onRemove={(idx) => removeItem("value_map", "gain_creators", idx)}
                    addPlaceholder={t('vpc_add_item')}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ValuePropositionStep;
